import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { faqs, siteMeta } from "@/mocks/community";

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const jsonLd = useMemo(() => {
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "常见问题", item: `${origin}/faq` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: "常见问题 | 爆料社区投稿、热榜与隐私规则说明",
    description:
      "解答爆料社区常见问题：内容来源、活跃话题榜的推荐机制、如何参与讨论、投稿是否匿名、以及隐私与举报处理流程，帮助你更快上手使用爆料社区。",
    keywords: "爆料社区,常见问题,投稿规则,话题榜机制,隐私举报",
    path: "/faq",
    type: "website",
    jsonLd,
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-container px-4 pb-10 pt-24 md:px-6 md:pb-12 md:pt-28">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />
          <div className="mt-4 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-400" aria-hidden="true" />
              Help Center
            </span>
            <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">常见问题</h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              关于浏览、参与讨论、投稿与内容规范，这些是大家问得最多的问题。如果没有找到答案，欢迎直接联系我们。
            </p>
          </div>
        </div>
      </div>

      <div className="bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid max-w-container gap-8 px-4 md:px-6 lg:grid-cols-[1.8fr_1fr]">
          <div className="space-y-3">
            {faqs.map((item, index) => {
              const open = openIndex === index;
              return (
                <div
                  key={item.question}
                  className={`overflow-hidden rounded-xl border bg-background-50 transition-colors ${
                    open ? "border-primary-300" : "border-background-200"
                  }`}
                >
                  <h2>
                    <button
                      type="button"
                      onClick={() => setOpenIndex(open ? null : index)}
                      aria-expanded={open}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-primary-50"
                    >
                      <span className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-100 text-xs font-bold text-primary-700">
                          {index + 1}
                        </span>
                        <span className="text-sm font-semibold text-foreground-950 md:text-base">{item.question}</span>
                      </span>
                      <i
                        className={`shrink-0 text-lg text-foreground-500 ${open ? "ri-subtract-line" : "ri-add-line"}`}
                        aria-hidden="true"
                      />
                    </button>
                  </h2>
                  {open && (
                    <div className="border-t border-background-200 px-5 py-4">
                      <p className="pl-9 text-sm leading-loose text-foreground-600">{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <aside className="flex flex-col gap-6 lg:sticky lg:top-24 lg:self-start">
            <section className="rounded-xl border border-primary-200 bg-primary-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-send-plane-line text-primary-600" aria-hidden="true" />
                还有别的疑问？
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                直接把你的问题写进投稿表单，编辑会在 1-2 个工作日内回复。匿名投稿同样有效。
              </p>
              <Link
                to="/about#submit"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                前往联系与投稿
              </Link>
            </section>

            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">快速导航</h2>
              <ul className="mt-3 space-y-2 text-sm">
                <li>
                  <Link to="/topics" className="flex items-center justify-between gap-2 text-foreground-700 transition-colors hover:text-primary-600">
                    今日活跃话题榜
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </Link>
                </li>
                <li>
                  <Link to="/about#principles" className="flex items-center justify-between gap-2 text-foreground-700 transition-colors hover:text-primary-600">
                    内容原则说明
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="flex items-center justify-between gap-2 text-foreground-700 transition-colors hover:text-primary-600">
                    关于爆料社区
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </Link>
                </li>
              </ul>
              <div className="mt-4 border-t border-background-200 pt-4">
                <p className="text-xs text-foreground-500">联系邮箱</p>
                <a
                  href={`mailto:${siteMeta.email}`}
                  className="mt-1 block text-sm font-medium text-foreground-900 transition-colors hover:text-primary-600"
                >
                  {siteMeta.email}
                </a>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}